import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AllocateCreditsResponse, BalanceContent } from "../types";
import { allocateCredits } from "../api";

export const useAllocateCredits = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AllocateCreditsResponse,
    Error,
    { userId: string; amount: number },
    { previousCredits: BalanceContent | null }
  >({
    mutationFn: ({ userId, amount }) => allocateCredits(userId, amount),
    onMutate: async (variables) => {
      // Cancel any ongoing refetches to avoid overwriting the optimistic update
      await queryClient.cancelQueries({ queryKey: ["balance"] });

      // Get the current credits data
      const previousCredits =
        queryClient.getQueryData<BalanceContent>(["balance"]) || null;

      // Optimistically update the credits data
      if (previousCredits) {
        queryClient.setQueryData<BalanceContent>(["balance"], (old) => ({
          ...old!,
          businessBalance: (old?.businessBalance ?? 0) - variables.amount,
        }));
      }

      // Return the previous credits data for rollback on error
      return { previousCredits };
    },
    onError: (_error, _variables, context) => {
      // Rollback the optimistic update on error
      if (context?.previousCredits) {
        queryClient.setQueryData<BalanceContent>(
          ["credits"],
          context.previousCredits
        );
      }
    },
    onSettled: () => {
      // Refetch the credits data and transaction history to ensure they're up-to-date
      queryClient.invalidateQueries({ queryKey: ["balance"] });
      queryClient.invalidateQueries({ queryKey: ["balanceHistory"] });
    },
  });
};
